/**
 * MarkerManagement Module
 * 
 * Handles bid markers, popups, and marker interactions including:
 * - Marker creation and styling
 * - Enhanced popup generation
 * - Marker interactions and hover effects
 * - Bid data processing
 */

import { MapUtilities } from './utilities';
import Logger from '../../lib/logger';

export class MarkerManagement {
  constructor(controller) {
    this.controller = controller;
  }

  // Create bid marker with enhanced popup functionality
  createBidMarker(v, icon, popupHtml) {
    const marker = L.marker([parseFloat(v.latitude), parseFloat(v.longitude)], { icon })
      .bindPopup(popupHtml, {
        closeButton: true,
        autoClose: true,
        closeOnClick: true,
        className: 'hover-popup',
        offset: [0, -10] // Slight offset to improve hover experience
      });
    
    // Store timeout reference for cleanup
    let closeTimeout = null;
    
    // Only add hover listeners if showPopupOnHover is enabled
    if (this.controller.showPopupOnHoverValue) {
      // Open popup on hover
      marker.on('mouseover', function() {
        // Clear any existing timeout
        if (closeTimeout) {
          clearTimeout(closeTimeout);
          closeTimeout = null;
        }
        this.openPopup();
      });
      
      // Close popup on mouseout with delay
      marker.on('mouseout', function(e) {
        closeTimeout = setTimeout(() => {
          // Simple approach: just close the popup after delay
          // The popup's own mouse events will handle keeping it open if needed
          this.closePopup();
        }, 200); // Slightly longer delay for better UX
      });
      
      // Handle popup interactions
      marker.on('popupopen', function() {
        const popupElement = this.getPopup()._container;
        if (popupElement) {
          // Keep popup open when hovering over it
          popupElement.addEventListener('mouseenter', () => {
            if (closeTimeout) {
              clearTimeout(closeTimeout);
              closeTimeout = null;
            }
          });
          
          // Close when leaving popup
          popupElement.addEventListener('mouseleave', () => {
            closeTimeout = setTimeout(() => {
              this.closePopup();
            }, 100);
          });
        }
      });
      
      // Cleanup timeout on popup close
      marker.on('popupclose', function() {
        if (closeTimeout) {
          clearTimeout(closeTimeout);
          closeTimeout = null;
        }
      });
    }
    // If showPopupOnHover is false, default Leaflet click behavior applies
    
    return marker;
  }

  // Add bids to map with enhanced processing
  addBids(jsonData) {
    try {
      // Handle both new structured format and legacy array format
      const addresses = this.extractAddressesFromData(jsonData);
      
      // Store the addresses data for provider extraction (even if empty)
      this.controller.currentBidAddresses = addresses;
      
      // Track if layer was already on map - we'll restore this state after updating
      const wasOnMap = this.controller.bids && this.controller.map.hasLayer(this.controller.bids);
      
      Logger.debug('addBids called', { 
        wasOnMap,
        addressesCount: addresses.length 
      });
      
      // Remove from map if already there to ensure clean update
      if (wasOnMap) {
        this.controller.map.removeLayer(this.controller.bids);
        Logger.debug('Removed bids layer from map for clean update');
      }
      
      // Always clear existing markers instead of re-creating the group
      // to ensure the reference on the map remains valid
      if (this.controller.bids) {
        this.controller.bids.clearLayers();
      } else {
        this.controller.bids = L.layerGroup();
      }
      
      // If no addresses, we're done (layer is now empty)
      if (addresses.length === 0) {
        Logger.debug('No addresses to display - layer cleared');
        
        // Still add back to map if it was there (now showing empty layer)
        if (wasOnMap) {
          this.controller.bids.addTo(this.controller.map);
        }
        
        // Update bid providers tab
        this.autoUpdateBidProvidersTab();
        return;
      }
      
      // Create icons once for reuse
      const icons = this.createBidIcons();
      
      // Process each address with error handling
      addresses.forEach(address => {
        try {
          if (!this.isValidAddress(address)) {
            Logger.warn('Invalid address data', { address });
            return;
          }
          
          const { icon, status, statusText } = this.determineMarkerType(address, icons);
          const html = this.createEnhancedPopup(address, status, statusText);
          const marker = this.createBidMarker(address, icon, html);
          
          // Store address data reference in marker for provider extraction
          marker.addressData = address;
          
          marker.addTo(this.controller.bids);
        } catch (error) {
          Logger.error('Error processing address', error, { address });
          // Continue with other addresses instead of failing completely
        }
      });
      
      // Only add back to map if it was already there before
      // This preserves the user's toggle state
      if (wasOnMap) {
        this.controller.bids.addTo(this.controller.map);
        
        // Ensure bids appear above circle if circle is present
        this.ensureProperLayerOrdering();
        Logger.debug('Added bids layer back to map (wasOnMap was true)');
      } else {
        Logger.debug('Not adding bids layer back to map (wasOnMap was false)');
      }
      
      Logger.debug('addBids completed', { 
        layerOnMap: this.controller.map.hasLayer(this.controller.bids) 
      });
      
      // Auto-update bid providers tab if it's currently active
      this.autoUpdateBidProvidersTab();
    } catch (error) {
      Logger.error('Failed in addBids', error);
      // Ensure we don't leave the controller in an inconsistent state
    }
  }

  // Extract addresses from various data formats
  extractAddressesFromData(jsonData) {
    try {
      if (jsonData && jsonData.addresses && Array.isArray(jsonData.addresses)) {
        return jsonData.addresses;
      } else if (Array.isArray(jsonData)) {
        return jsonData;
      } else {
        Logger.warn('Invalid data format received in addBids', { jsonData });
        return [];
      }
    } catch (error) {
      Logger.error('Error extracting addresses from data', error);
      return [];
    }
  }

  // Validate address data
  isValidAddress(address) {
    try {
      return address && 
             typeof address.latitude !== 'undefined' && 
             typeof address.longitude !== 'undefined' &&
             !isNaN(parseFloat(address.latitude)) &&
             !isNaN(parseFloat(address.longitude));
    } catch (error) {
      Logger.warn('Error validating address', { error, address });
      return false;
    }
  }

  // Create all bid icons - uses instance method for configurable icon URLs
  createBidIcons() {
    try {
      // Use instance method for icons (Phase 1 refactoring)
      const icons = this.controller.getIcons();
      const { FLAG_ICON_ANCHOR, ICON_ANCHOR } = this.controller.constructor.CONSTANTS;
      
      return {
        bids: this.createIcon(icons.BIDS, { anchor: ICON_ANCHOR }),
        noBids: this.createIcon(icons.NO_BIDS, { anchor: ICON_ANCHOR }),
        active: this.createIcon(icons.ACTIVE, { anchor: FLAG_ICON_ANCHOR }),
        orders: this.createIcon(icons.ORDERS, { anchor: FLAG_ICON_ANCHOR })
      };
    } catch (error) {
      Logger.error('Error creating bid icons', error);
      // Return default icons to prevent complete failure
      return {
        bids: null,
        noBids: null,
        active: null,
        orders: null
      };
    }
  }

  // Create standardized icon
  createIcon(iconUrl, options = {}) {
    try {
      const { ICON_SIZE, ICON_ANCHOR, POPUP_ANCHOR } = this.controller.constructor.CONSTANTS;
      
      return L.icon({
        iconUrl,
        iconSize: ICON_SIZE,
        iconAnchor: options.anchor || ICON_ANCHOR,
        popupAnchor: options.popupAnchor || POPUP_ANCHOR
      });
    } catch (error) {
      Logger.error('Error creating icon', error, { iconUrl });
      return null;
    }
  }

  // Determine marker type based on address data
  determineMarkerType(address, icons) {
    try {
      if (address.circuits > 0) {
        if (address.activecircuits > 0) {
          return {
            icon: icons.active,
            status: 'active',
            statusText: 'Active Circuits'
          };
        } else {
          return {
            icon: icons.orders,
            status: 'orders', 
            statusText: 'Orders'
          };
        }
      } else {
        if (address.bids > 0) {
          return {
            icon: icons.bids,
            status: 'bids',
            statusText: 'Bids Available'
          };
        } else {
          return {
            icon: icons.noBids,
            status: 'nobids',
            statusText: 'No Bids'
          };
        }
      }
    } catch (error) {
      Logger.error('Error determining marker type', error, { address });
      return {
        icon: icons.noBids,
        status: 'nobids',
        statusText: 'No Bids'
      };
    }
  }

  /**
   * Create enhanced popup from HTML template (Phase 2 - Option A)
   *
   * This method uses an HTML <template> element defined in the view partial
   * instead of hardcoded HTML strings. Benefits:
   * - HTML stays in ERB templates where it belongs
   * - CSP compliant (no inline scripts)
   * - No N+1 queries (uses already-loaded API data)
   * - Easier to maintain and customize
   *
   * @param {Object} addressData - Address data from API
   * @param {string} status - Status type (active, orders, bids, nobids)
   * @param {string} statusText - Human-readable status text
   * @returns {string} - HTML string for popup content
   */
  createEnhancedPopup(addressData, status, statusText) {
    try {
      // Try to use template from DOM (Phase 2 approach)
      const template = document.getElementById('map-popup-template');
      
      if (template) {
        return this.createPopupFromTemplate(template, addressData, status, statusText);
      }
      
      // Fallback to inline generation for backward compatibility
      Logger.warn('map-popup-template not found, using inline fallback. Add template to view for Phase 2 compliance.');
      return this.createPopupInline(addressData, status, statusText);
    } catch (error) {
      Logger.error('Error creating enhanced popup', error, { addressData, status });
      // Return minimal fallback to prevent complete failure
      return this.createPopupInline(addressData, status, statusText);
    }
  }

  /**
   * Create popup from HTML template (Phase 2 - Option A implementation)
   * Clones template and fills with data attributes
   */
  createPopupFromTemplate(template, addressData, status, statusText) {
    try {
      // SECURITY: Sanitize all user-provided data to prevent XSS attacks
      const escape = this.controller.escapeHtml.bind(this.controller);
      
      // Clone template content
      const content = template.content.cloneNode(true);
      const popup = content.querySelector('.enhanced-popup');
      
      if (!popup) {
        Logger.error('Template missing .enhanced-popup element', { template });
        return this.createPopupInline(addressData, status, statusText);
      }
      
      // Get API endpoints from controller
      const endpoints = this.controller.getApiEndpoints();
      
      // Ensure numeric values are actually numbers
      const circuits = parseInt(addressData.circuits) || 0;
      const activecircuits = parseInt(addressData.activecircuits) || 0;
      const bids = parseInt(addressData.bids) || 0;
      const addressId = parseInt(addressData.id) || 0;
      
      // Set status class and icon on header
      const header = popup.querySelector('.popup-header');
      if (header) {
        header.classList.add(this.getStatusClass(status));
        const statusIconEl = header.querySelector('[data-popup-slot="status-icon"]');
        if (statusIconEl) {
          statusIconEl.className = this.getStatusIcon(status);
        }
        const statusTextEl = header.querySelector('[data-popup-slot="status-text"]');
        if (statusTextEl) {
          statusTextEl.textContent = escape(statusText);
        }
      }
      
      // Distance info
      const distanceContainer = popup.querySelector('[data-popup-slot="distance"]');
      if (distanceContainer) {
        if (addressData.distance_km) {
          const distanceValue = distanceContainer.querySelector('[data-popup-slot="distance-value"]');
          if (distanceValue) {
            distanceValue.textContent = parseFloat(addressData.distance_km).toFixed(2);
          }
          distanceContainer.classList.remove('d-none');
        } else {
          distanceContainer.classList.add('d-none');
        }
      }
      
      // Address info
      const addressMain = popup.querySelector('[data-popup-slot="address-main"]');
      if (addressMain) {
        const street = escape(addressData.street || 'N/A');
        const streetNumber = escape(addressData.street_number || '');
        addressMain.textContent = `${street}, ${streetNumber}`;
      }
      
      const addressDetails = popup.querySelector('[data-popup-slot="address-details"]');
      if (addressDetails) {
        const neighborhood = escape(addressData.neighborhood || '');
        const zipCode = escape(addressData.zip_code || '');
        addressDetails.textContent = `${neighborhood} ${zipCode}`;
      }
      
      // Circuit stats (conditional)
      const circuitsContainer = popup.querySelector('[data-popup-slot="circuits-stat"]');
      if (circuitsContainer) {
        if (circuits > 0) {
          const circuitsValue = circuitsContainer.querySelector('[data-popup-slot="circuits-value"]');
          if (circuitsValue) {
            circuitsValue.textContent = circuits;
          }
          const activeContainer = circuitsContainer.querySelector('[data-popup-slot="active-circuits"]');
          if (activeContainer) {
            if (activecircuits > 0) {
              const activeValue = activeContainer.querySelector('[data-popup-slot="active-value"]');
              if (activeValue) {
                activeValue.textContent = activecircuits;
              }
              activeContainer.classList.remove('d-none');
            } else {
              activeContainer.classList.add('d-none');
            }
          }
          circuitsContainer.classList.remove('d-none');
        } else {
          circuitsContainer.classList.add('d-none');
        }
      }
      
      // Bids stats (conditional)
      const bidsContainer = popup.querySelector('[data-popup-slot="bids-stat"]');
      if (bidsContainer) {
        if (bids > 0) {
          const bidsValue = bidsContainer.querySelector('[data-popup-slot="bids-value"]');
          if (bidsValue) {
            bidsValue.textContent = bids;
          }
          bidsContainer.classList.remove('d-none');
        } else {
          bidsContainer.classList.add('d-none');
        }
      }
      
      // Action buttons
      const viewBidsBtn = popup.querySelector('[data-popup-slot="view-bids-btn"]');
      if (viewBidsBtn) {
        if (bids > 0) {
          viewBidsBtn.href = `${endpoints.BIDS_CRM}?address_id=${addressId}&compact=1`;
          viewBidsBtn.classList.remove('d-none');
        } else {
          viewBidsBtn.classList.add('d-none');
        }
      }
      
      const viewQuotesBtn = popup.querySelector('[data-popup-slot="view-quotes-btn"]');
      if (viewQuotesBtn) {
        const circuitsParam = circuits > 0 ? '&worders=1' : '';
        viewQuotesBtn.href = `${endpoints.QUOTES_CRM}?address_id=${addressId}${circuitsParam}`;
      }
      
      // Create a temporary container to get HTML string
      const temp = document.createElement('div');
      temp.appendChild(content);
      return temp.innerHTML;
    } catch (error) {
      Logger.error('Error creating popup from template', error, { 
        addressData, 
        status,
        templateExists: !!template 
      });
      return this.createPopupInline(addressData, status, statusText);
    }
  }

  /**
   * Fallback inline popup generation (backward compatibility)
   * @deprecated Use template-based approach instead
   */
  createPopupInline(addressData, status, statusText) {
    try {
      // SECURITY: Sanitize all user-provided data to prevent XSS attacks
      const escape = this.controller.escapeHtml.bind(this.controller);
      
      // Get API endpoints from controller (configurable instead of hardcoded)
      const endpoints = this.controller.getApiEndpoints();
      
      // Sanitize address fields
      const street = escape(addressData.street || 'N/A');
      const streetNumber = escape(addressData.street_number || '');
      const neighborhood = escape(addressData.neighborhood || '');
      const zipCode = escape(addressData.zip_code || '');
      
      // Distance info (numbers are safe, but sanitize for consistency)
      const distanceInfo = addressData.distance_km ?
        `<div class="popup-distance">
          <i class="fas fa-route"></i> ${parseFloat(addressData.distance_km).toFixed(2)} km away
        </div>` : ''
      
      const statusClass = this.getStatusClass(status)
      const statusIcon = this.getStatusIcon(status)
      
      // Sanitize status text
      const safeStatusText = escape(statusText);
      
      // Ensure numeric values are actually numbers
      const circuits = parseInt(addressData.circuits) || 0;
      const activecircuits = parseInt(addressData.activecircuits) || 0;
      const bids = parseInt(addressData.bids) || 0;
      const addressId = parseInt(addressData.id) || 0;
      
      return `
        <div class="enhanced-popup">
          <div class="popup-header ${statusClass}">
            <div class="popup-status">
              <i class="${statusIcon}"></i>
              <span class="status-text">${safeStatusText}</span>
            </div>
            ${distanceInfo}
          </div>
          
          <div class="popup-content">
            <div class="popup-address">
              <i class="fas fa-map-marker-alt"></i>
              <div class="address-info">
                <div class="address-main">${street}, ${streetNumber}</div>
                <div class="address-details">${neighborhood} ${zipCode}</div>
              </div>
            </div>
            
            <div class="popup-stats">
              ${circuits > 0 ? `
                <div class="stat-item">
                  <i class="fas fa-network-wired"></i>
                  <span class="stat-label">Circuits:</span>
                  <span class="stat-value">${circuits}</span>
                  ${activecircuits > 0 ? `<span class="stat-active">(${activecircuits} active)</span>` : ''}
                </div>
              ` : ''}
              
              ${bids > 0 ? `
                <div class="stat-item">
                  <i class="fas fa-gavel"></i>
                  <span class="stat-label">Bids:</span>
                  <span class="stat-value">${bids}</span>
                </div>
              ` : ''}
            </div>
          </div>
          
          <div class="popup-actions">
            ${bids > 0 ? `
              <a href="${endpoints.BIDS_CRM}?address_id=${addressId}&compact=1"
                 data-turbo-frame="bidscrm"
                 data-action="click->map-leaflet#activateBidListTab"
                 class="btn btn-sm btn-primary text-decoration-none">
                <i class="fas fa-eye"></i> View Bids
              </a>
            ` : ''}
            <a href="${endpoints.QUOTES_CRM}?address_id=${addressId}${circuits > 0 ? '&worders=1' : ''}"
               data-turbo-frame="crm"
               class="btn btn-sm btn-secondary text-decoration-none">
              <i class="fas fa-file-invoice-dollar"></i> View Quotes
            </a>
          </div>
        </div>
      `
    } catch (error) {
      Logger.error('Error creating inline popup', error, { addressData, status });
      // Return minimal fallback to prevent complete failure
      return `<div class="enhanced-popup"><p>Error generating popup</p></div>`;
    }
  }

  // Get CSS class for status
  getStatusClass(status) {
    const statusClasses = {
      'active': 'status-active',
      'orders': 'status-orders', 
      'bids': 'status-bids',
      'nobids': 'status-nobids'
    }
    return statusClasses[status] || 'status-nobids'
  }

  // Get icon for status
  getStatusIcon(status) {
    const statusIcons = {
      'active': 'fas fa-bolt',
      'orders': 'fas fa-clipboard-list',
      'bids': 'fas fa-gavel',
      'nobids': 'fas fa-exclamation-circle'
    }
    return statusIcons[status] || 'fas fa-map-marker-alt';
  }

  // Ensure proper layer ordering (circle behind, markers/popups in front)
  ensureProperLayerOrdering() {
    MapUtilities.ensureProperLayerOrdering(this.controller);
  }

  // Auto-update bid providers tab if it's currently active
  autoUpdateBidProvidersTab() {
    MapUtilities.autoUpdateBidProvidersTab(this.controller, 500);
  }

  // Center map on location - uses instance method for zoom level
  centerOnLocation(event) {
    try {
      event.preventDefault();
      event.stopPropagation();
      
      const lat = parseFloat(event.target.dataset.lat);
      const lng = parseFloat(event.target.dataset.lng);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        // Use instance method for CENTER_ZOOM (Phase 1 refactoring)
        this.controller.map.setView([lat, lng], this.controller.CENTER_ZOOM);
        Logger.debug(`Centered map on location`, { lat, lng });
      }
    } catch (error) {
      Logger.error('Failed to center on location', error, { event });
    }
  }

  // Format address for display
  formatAddressForDisplay(address) {
    try {
      const parts = [];
      
      if (address.street) {
        parts.push(address.street);
        if (address.street_number) {
          parts[0] += ` ${address.street_number}`;
        }
      }
      
      if (address.neighborhood) {
        parts.push(address.neighborhood);
      }
      
      return parts.length > 0 ? parts.join(', ') : 'Address not available';
    } catch (error) {
      Logger.error('Error formatting address for display', error, { address });
      return 'Address not available';
    }
  }
}